import { Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useRef, useState, useCallback } from "react";
import { formatDateForStorage, isValidDate } from "@/lib/utils/date-utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setHours, setMinutes, format } from "date-fns";

interface ScheduleDetailsCardProps {
  newCourse: any;
  setNewCourse: (course: any) => void;
}

export const ScheduleDetailsCard = ({
  newCourse,
  setNewCourse
}: ScheduleDetailsCardProps) => {
  // Update parent state directly on change
  const handleDateChange = (field: string, value: string) => {
    setNewCourse((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // For react-datepicker, handle time selection and store as 'HH:mm'
  const handleTimeChange = (field: string, date: Date | null) => {
    const value = date ? format(date, "HH:mm") : "";
    setNewCourse((prev: any) => ({
          ...prev,
          [field]: value
        }));
  };

  if (newCourse.event_type !== "live") return null;

  const getFieldError = (field: string) => {
    if (field === 'dates') {
      if (!newCourse.start_date) return 'Start date is required for live events';
      if (!newCourse.end_date) return 'End date is required for live events';
      if (isValidDate(newCourse.start_date) && isValidDate(newCourse.end_date)) {
        const startDate = new Date(newCourse.start_date);
        const endDate = new Date(newCourse.end_date);
        if (startDate > endDate) return 'End date must be after start date';
      }
    }
    if (field === 'times') {
      if (!newCourse.start_time) return 'Start time is required for live events';
      if (!newCourse.end_time) return 'End time is required for live events';
      if (newCourse.start_date === newCourse.end_date && newCourse.start_time >= newCourse.end_time) {
        return 'End time must be after start time';
      }
    }
    return '';
  };

  return (
    <Card className="p-6 border-accent/20">
      <div className="flex items-center gap-2 mb-6">
        <Calendar className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-title font-semibold text-primary">Schedule Details</h2>
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-base font-serif">Start Date *</Label>
            <Input
              type="date"
              value={newCourse.start_date}
              onChange={e => handleDateChange('start_date', e.target.value)}
              className={`font-sans ${getFieldError('dates') && !newCourse.start_date ? 'border-destructive' : ''}`}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-base font-serif">End Date *</Label>
            <Input
              type="date"
              value={newCourse.end_date}
              onChange={e => handleDateChange('end_date', e.target.value)}
              className={`font-sans ${getFieldError('dates') && !newCourse.end_date ? 'border-destructive' : ''}`}
            />
          </div>
          {getFieldError('dates') && (
            <p className="text-sm text-destructive col-span-2 font-sans">{getFieldError('dates')}</p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-base font-serif">Start Time *</Label>
            <DatePicker
              selected={newCourse.start_time ? new Date(`2000-01-01T${newCourse.start_time}`) : null}
              onChange={(date: Date | null) => handleTimeChange('start_time', date)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans ${getFieldError('times') && !newCourse.start_time ? 'border-destructive' : ''}`}
              wrapperClassName="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-base font-serif">End Time *</Label>
            <DatePicker
              selected={newCourse.end_time ? new Date(`2000-01-01T${newCourse.end_time}`) : null}
              onChange={(date: Date | null) => handleTimeChange('end_time', date)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans ${getFieldError('times') && !newCourse.end_time ? 'border-destructive' : ''}`}
              wrapperClassName="w-full"
            />
          </div>
          {getFieldError('times') && (
            <p className="text-sm text-destructive col-span-2 font-sans">{getFieldError('times')}</p>
          )}
        </div>
      </div>
    </Card>
  );
};
